import { ReportType } from '@/lib/api/schemas/reports';
import {
  SubLocationsOfLocationListType,
  SubLocationType,
} from '@/lib/api/schemas/sublocations';
import {
  AppMetadataSchema,
  AppMetadataType,
  RawAppMetadataType,
} from '@/lib/api/schemas/user-management';

/**
 * Wildcard utilities for sublocation access management
 */
export class WildcardSublocation {
  static readonly ID = '*' as const;
  static readonly DISPLAY_NAME = 'All Sublocations' as const;

  /**
   * Creates a wildcard SubLocationType object for form usage
   */
  static create(): SubLocationType {
    return {
      subLocationId: this.ID,
      displayName: this.DISPLAY_NAME,
      duration: null,
      approvalCode: '',
      validationAccessCode: '',
      approvalCodeLastUpdated: null,
      validationAccessCodeDistributionList: null,
      validationAccessCodeSMSDistributionList: null,
      validationAccessCodeLastUpdated: null,
      billing: {
        enabled: false,
        is_billing_required: false,
        monthly_quantity: 0,
        monthly_saas_fee: 0,
        payment_method: null,
        quantity_tracker: null,
        is_waiving_parking_fee: false,
        billing_type: 'count',
        monthly_parking_charge_day: 7,
      },
      validationTimeGateMinutes: null,
    };
  }

  /**
   * Checks if a sublocation ID is the wildcard
   */
  static isWildcard(subLocationId: string): boolean {
    return subLocationId === this.ID;
  }

  /**
   * Checks if an array of sublocation IDs contains only the wildcard
   */
  static hasWildcardAccess(sublocations: string[]): boolean {
    return sublocations.length === 1 && this.isWildcard(sublocations[0]!);
  }

  /**
   * Checks if an array of SubLocationType objects contains the wildcard
   */
  static hasWildcardInSublocations(sublocations: SubLocationType[]): boolean {
    return (
      sublocations.length === 1 &&
      this.isWildcard(sublocations[0]?.subLocationId ?? '')
    );
  }
}

export class AppMetadata {
  private readonly metadata: AppMetadataType | null;

  constructor(metadata: RawAppMetadataType) {
    const appMetadataParseResult = AppMetadataSchema.safeParse(metadata);
    if (appMetadataParseResult.success) {
      this.metadata = appMetadataParseResult.data;
    } else {
      this.metadata = null;
    }
  }

  hasAccessToPortal(): boolean {
    return !!this.metadata?.has_access_to_portal;
  }

  hasAllLocations(): boolean {
    return !!this.metadata?.all_locations;
  }

  isGovOfficer(): boolean {
    return !!this.metadata?.is_gov_officer;
  }

  hasAccessToLocation(locationId: string) {
    const locations = this.metadata?.locations;
    if (!locations) return false;

    for (const [rootLocationId, boundary] of Object.entries(locations)) {
      // user has sublocations assigned and not to all of them meaning by default no access to root location
      const hasAnySublocations = boundary.sublocations?.length >= 1;
      const hasAllSublocations = WildcardSublocation.hasWildcardAccess(
        boundary.sublocations ?? []
      );
      const hasAccessToRoot = boundary.has_access_to_root_location;

      if (!hasAccessToRoot && hasAnySublocations && !hasAllSublocations) {
        continue;
      }

      if (locationId === rootLocationId) {
        return true;
      }
    }

    return false;
  }

  hasAccessToSublocation(locationId: string, sublocationId: string) {
    const boundary = this.metadata?.locations?.[locationId];
    if (!boundary) return false;

    const sublocations: string[] = boundary.sublocations ?? [];
    // wildcard grants access to all sublocations
    if (WildcardSublocation.hasWildcardAccess(sublocations)) return true;

    return sublocations.includes(sublocationId);
  }

  /**
   * True when the user may treat this main lot as whole-lot scope (root access or
   * wildcard sub-lot access), used to decide whether they may see full location_group UI.
   */
  hasFullMainLotScope(locationId: string): boolean {
    const boundary = this.metadata?.locations?.[locationId];
    if (!boundary) {
      return false;
    }
    if (boundary.has_access_to_root_location) {
      return true;
    }
    return WildcardSublocation.hasWildcardAccess(boundary.sublocations ?? []);
  }

  /**
   * When the user has no root-lot access and exactly one concrete sub-lot on this
   * location, returns that sub-lot id (for validator-scoped permit UI). Otherwise null.
   */
  getSoleValidatorSubLocationId(locationId: string): string | null {
    const boundary = this.metadata?.locations?.[locationId];
    if (!boundary) {
      return null;
    }
    if (boundary.has_access_to_root_location) {
      return null;
    }
    const sublocations = boundary.sublocations ?? [];
    if (WildcardSublocation.hasWildcardAccess(sublocations)) {
      return null;
    }
    if (sublocations.length !== 1) {
      return null;
    }
    return sublocations[0] ?? null;
  }

  hasAccessToReport(reportType: ReportType) {
    return this.metadata?.report_types?.includes(reportType) ?? false;
  }

  getAllowedLocationIds(): string[] {
    return Object.keys(this.metadata?.locations ?? {});
  }

  /**
   * Checks if user has any form of access to a location (root access OR sublocation access)
   * @param locationId - The location ID to check
   * @returns true if user has any access to the location
   */
  private hasAnyAccessToLocation(locationId: string): boolean {
    // Check if location exists in user's metadata
    const boundary = this.metadata?.locations?.[locationId];
    if (!boundary) return false;

    // User has access if they have root access OR any sublocation access
    const hasRootAccess = this.hasAccessToLocation(locationId);
    const hasSublocations =
      boundary.sublocations && boundary.sublocations.length > 0;

    return hasRootAccess || hasSublocations;
  }

  getAssignedReportTypes(): ReportType[] {
    return (this.metadata?.report_types ?? []) as ReportType[];
  }

  /**
   * Resolves the user's accessible locations and sublocations from backend app_metadata
   * into a structured list based on the available locations. Creates an intersection of
   * what's available from the API and what the user has permission to access.
   *
   * @param availableLocations - All available locations from the API
   * @returns SubLocationsOfLocationListType representing the intersection of available and accessible locations
   */
  resolveLocationsAndSublocations(
    availableLocations: SubLocationsOfLocationListType
  ): SubLocationsOfLocationListType {
    const result: SubLocationsOfLocationListType = [];

    // Iterate through available locations (what API returned)
    availableLocations.forEach((availableLocation) => {
      const locationId = availableLocation.locationId;

      // Check if user has ANY access to this location (root OR sublocation access)
      if (!this.hasAnyAccessToLocation(locationId)) return;

      const locationData = { ...availableLocation };

      // Whole-main-lot scope is reported as root access: a wildcard boundary already
      // grants the root lot in `hasAccessToLocation`, so the resolved shape must not
      // contradict it.
      locationData.hasAccessToRootLocation =
        this.hasFullMainLotScope(locationId);

      // Handle sublocation intersection
      if (this.hasAccessToSublocation(locationId, WildcardSublocation.ID)) {
        locationData.sublocations = [WildcardSublocation.create()];
      } else {
        // User has specific sublocation permissions - intersect with available.
        // Guard against a "Location Only" location returned without a
        // `sublocations` array (ENG-1714) so the intersection can't crash.
        const accessibleSublocations = (
          availableLocation.sublocations ?? []
        ).filter((subloc) =>
          this.hasAccessToSublocation(locationId, subloc.subLocationId)
        );

        // Set the intersected sublocations
        locationData.sublocations = accessibleSublocations;

        // If user has location access but no sublocation intersection,
        // it means root-only access (empty sublocations array)
        // This is already handled by the filter above resulting in empty array
      }

      // Only add location if user has some form of meaningful access
      // (either has sublocations access or root-only access)
      const hasRootAccess = this.hasAccessToLocation(locationId);
      const hasSublocationsAccess = locationData.sublocations.length > 0;
      const hasWildcardAccess = this.hasAccessToSublocation(
        locationId,
        WildcardSublocation.ID
      );

      if (hasRootAccess || hasSublocationsAccess || hasWildcardAccess) {
        result.push(locationData);
      }
    });

    return result;
  }
}
